import { Component, ErrorInfo, ReactNode } from 'react';
import { InitializationErrorScreen } from './initialize-error-screen';
import { classifyError, ErrorClassification } from '../../../util/telemetry/error/error-classifier.util';
import { logError } from '../../../util/telemetry/error/log-error.util';

// Track errors caught by React's lifecycle to prevent duplicate/misclassified global window logs
const handledReactErrors = new WeakSet<object>();

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  classification: ErrorClassification | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    classification: null
  };

  public static getDerivedStateFromError(error: Error): State {
    if (error && typeof error === 'object') {
      handledReactErrors.add(error);
    }
    const classification = classifyError(error);
    return { hasError: true, classification };
  }

  private handleGlobalError = (event: ErrorEvent) => {
    const error = event.error || new Error(event.message);
    
    // Defer check to allow React's synchronous render recovery (getDerivedStateFromError) to run first
    setTimeout(() => {
      if (error && typeof error === 'object' && handledReactErrors.has(error)) {
        return;
      }
      logError(error, { category: 'unhandled_exception' });
    }, 0);
  };

  private handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    logError(error, { category: 'unhandled_rejection' });
  };

  public componentDidMount() {
    window.addEventListener('error', this.handleGlobalError);
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  public componentWillUnmount() {
    window.removeEventListener('error', this.handleGlobalError);
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const classification = classifyError(error);
    logError(error, { 
      category: 'react_render', 
      componentStack: errorInfo.componentStack
    });

    if (classification === 'CHUNK_ERROR') {
      const hasReloaded = sessionStorage.getItem('chunk_reload_attempted');
      // Perform a single automatic reload attempt for chunk load failures
      if (!hasReloaded) {
        sessionStorage.setItem('chunk_reload_attempted', 'true');
        window.location.reload();
      }
    }
  }

  public render() {
    if (this.state.hasError) {
      const classification = this.state.classification || 'UNKNOWN_ERROR';
      
      // We don't want to show the full screen error for AUTH_ERROR, as it typically gets redirected.
      // But if an AUTH_ERROR bubbles here, treat it as unknown/generic.
      const safeClassification = classification === 'AUTH_ERROR' ? 'UNKNOWN_ERROR' : classification;
      
      return <InitializationErrorScreen classification={safeClassification} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
